import { Box, Grid, Stack } from "@mui/material";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import RegistryData from "./scaleComponents/registryComponent";
import { theme } from "@/theme";
import InfoBodyData from "./scaleComponents/bofyInfoComponent";
import SmokeData from "./scaleComponents/smokeComponent";
import PressureData from "./scaleComponents/pressureComponent";
import CholesterolData from "./scaleComponents/cholesterolComponent";
import { useTranslatedMessages } from "@/multiLanguage/i18n";
import CardiovascularEventsData from "./scaleComponents/cardiovascularEventComponent";
import DiabetData from "./scaleComponents/diabetComponent";

import TakingDrugsData from "./scaleComponents/takingDrugsComponents";
import { CustomButton } from "@/components";
import { ClientData } from "@/services/pharmacyService";
import { useEffect } from "react";
import FamilyHycholData from "./scaleComponents/familyHycholComponent";
import PrivacyData from "./scaleComponents/privacyComponent";
import FooterFormData from "./scaleComponents/footerFormComponent";
import LipoproteinData from "./scaleComponents/lipoproteinComponent";
import CreatinineData from "./scaleComponents/creatinineComponent";
import { User } from "@/states/localStorageState";
import { constants } from "@/utils/constants";

const DefaultFormData: FormDataFree = {
    //ANAGRAFICA
    name: "Test",
    surname: "Test",
    country: "italy",
    sex: "male",
    age: 41,
    //Dati fisici
    height: 189,
    weight: 83,
    //Dati fumatore
    actualSmoker: false,
    exSmokerSwitch: false,
    exSmokerYears: 0,
    //Pressione
    systolicPressure: 150,
    diastolicPressure: 150,
    //Cholesterol
    totalCholesterol: 116,
    hdlCholesterol: 27,
    triglycerides: 150,
    //lipoproteina(a)
    lipoproteinValue: 30,
    lipoproteinUom: "nmol/L",
    //creatine
    creatinine: 10,
    //eventi cardiovasculari
    //cardiovascularEvent: false, // NON SERVE DA VEDERE COSA FARE LATO BK E SE SI PUO TOGLIERE
    cardiovascularEventSwitch: false,
    moreCardiovascularEvent: false,
    arteriopathy: false,
    arteryStenosis: false,
    multiDistrictStenosis: false,
    //family event
    familyHychol: false,
    //diabet data
    //diabetes: false, // NON SERVE DA VEDERE COSA FARE LATO BK E SE SI PUO TOGLIERE
    diabetesSwitch: false,
    diabetesSelect: "diabete1",
    diabetesAge: 0,
    diabetesDiseases: false,
    //medicine
    cholesterolMedications: false,
    hypertensionMedications: false,
    //privacy
    privacy_switch: false,
};
export type FormDataFree = {
    //ANAGRAFICA
    name?: string;
    surname?: string;
    country: string;
    sex: string;
    age: number;
    //Dati fisici
    height: number;
    weight: number;
    //Dati fumatore
    actualSmoker: boolean;
    exSmokerSwitch: boolean;
    exSmokerYears?: number;
    //Pressione
    systolicPressure: number;
    diastolicPressure: number;
    //Cholesterol
    totalCholesterol: number;
    hdlCholesterol: number;
    ldlCholesterol?: number;
    triglycerides: number;
    noHdlCholesterol?: number;
    //lipoproteina(a)
    lipoproteinValue?: number;
    lipoproteinUom?: string;
    //creatine
    creatinine: number;
    //eventi cardiovasculari
    //cardiovascularEvent: boolean, // NON SERVE DA VEDERE COSA FARE LATO BK E SE SI PUO TOGLIERE
    cardiovascularEventSwitch: boolean;
    moreCardiovascularEvent: boolean;
    arteriopathy: boolean;
    arteryStenosis: boolean;
    /**
     * Placche con stenosi > 50% o sintomatiche in più di un distretto arterioso.
     * Opzionale perché le compilazioni salvate prima della sua introduzione non
     * lo contengono.
     */
    multiDistrictStenosis?: boolean;
    //family event
    familyHychol: boolean;
    //diabet
    //diabetes: boolean, // NON SERVE DA VEDERE COSA FARE LATO BK E SE SI PUO TOGLIERE
    diabetesSwitch: boolean;
    diabetesSelect?: string;
    diabetesAge?: number;
    diabetesDiseases: boolean;
    //medicine
    cholesterolMedications: boolean;
    hypertensionMedications: boolean;
    privacy_switch?: boolean;
};

type FormProps = {
    onSubmit: SubmitHandler<FormDataFree>;
    selectedClient?: ClientData;
    user?: User;
    /** Solo per strumenti dev: riempie l'intero form con un caso predefinito. */
    prefillData?: FormDataFree;
};

export function FreeScaleHookForm({ onSubmit, selectedClient, user, prefillData }: FormProps) {
    const lang = useTranslatedMessages();
    const formMethods = useForm<FormDataFree>({
        mode: "onBlur",
        defaultValues: constants.IS_DEBUG ? { ...DefaultFormData } : undefined,
    });
    useEffect(() => {
        if (selectedClient) {
            formMethods.setValue("name", selectedClient.name);
            formMethods.setValue("surname", selectedClient.surname);
            formMethods.setValue("country", selectedClient.nationality);
        }
    }, [selectedClient]);

    useEffect(() => {
        if (prefillData) {
            formMethods.reset({ ...prefillData });
        }
    }, [prefillData]);

    const showNamesFields = user !== undefined;

    return (
        <FormProvider {...formMethods}>
            <Box
                sx={{
                    borderRadius: "20px 20px 20px 20px",
                    backgroundColor: theme.palette.primary.light,
                }}
            >
                <form onSubmit={formMethods.handleSubmit(onSubmit)}>
                    <Stack gap={3} p={3}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Stack gap={3}>
                                        <RegistryData
                                            showNamesFields={showNamesFields}
                                            disabledPatientData={selectedClient ? true : false}
                                            defaultCountry={selectedClient?.nationality}
                                            nameRequired={false}
                                            surnameRequired={false}
                                        />
                                        <InfoBodyData />
                                        {/* La creatinina fa da jolly per pareggiare le due
                                            colonne: senza nome e cognome la sinistra ha un
                                            campo in meno, quindi sta qui sotto il peso. */}
                                        {!showNamesFields && <CreatinineData creatinineRequired />}
                                    </Stack>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Stack gap={3}>
                                        <PressureData />
                                        <CholesterolData />
                                        {showNamesFields && <CreatinineData creatinineRequired />}
                                        <LipoproteinData />
                                    </Stack>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box>
                                    <Stack>
                                        <SmokeData />
                                        <CardiovascularEventsData multiDistrictStenosis />

                                        <FamilyHycholData />
                                        <DiabetData />
                                        <TakingDrugsData />

                                        <PrivacyData />
                                        <FooterFormData />
                                    </Stack>
                                </Box>
                            </Grid>
                        </Grid>
                    </Stack>
                    <Stack flexDirection={"row"} justifyContent={"center"} mt={2}>
                        <Box>
                            <CustomButton
                                sx={{
                                    backgroundColor: theme.palette.secondary.dark,
                                    color: theme.palette.primary.light,
                                }}
                                data-testid="form-component-submitButtom"
                                label={lang.scales.shared.forms.calculate}
                                type="submit"
                                onClick={() => {
                                    /*  console.log("EEEEEEEEEEEEEEEEEEEEEEEEEEE", formMethods.formState.isValid); console.log("BBBBBBBBBBBBBBBBB", formMethods.getValues()) */
                                }}
                            />
                        </Box>
                        {/* <Box >
                            <CustomButton
                                sx={{ backgroundColor: theme.palette.secondary.dark, color: theme.palette.primary.light }}

                                label="Errori"
                                onClick={() => printErrors(formMethods.formState.errors)}
                            />

                        </Box> */}
                    </Stack>
                </form>
            </Box>
        </FormProvider>
    );
}
